import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { SignatureCompletionData, useNavigation } from "../../../hooks";
import { useTransmissionDemandDetail } from "../../../hooks/useTransmissionDemandDetail";
import { InstitutionWithDatasets } from "../../../services/index";
import { updateTransmissionDemand } from "../../../services/institutionService";
import LoadingSpinner from "../../common/LoadingSpinner";
import MyDataAgreementPage from "../../common/MyDataAgreementPage";
import DataDeletionConfirmModal from "../../modals/DataDeletionConfirmModal";

const TransmissionDemandEditPage: React.FC = () => {
  const { transmissionDemandId } = useParams<{
    transmissionDemandId: string;
  }>();
  const { detail, loading } = useTransmissionDemandDetail(
    transmissionDemandId || ""
  );
  const { goBack } = useNavigation();

  // 데이터 삭제 모달 상태
  const [isDataDeletionModalOpen, setIsDataDeletionModalOpen] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const [modalResolve, setModalResolve] = useState<
    ((value: boolean) => void) | null
  >(null);

  if (!transmissionDemandId) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">잘못된 접근입니다.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">전송요구서 정보를 불러올 수 없습니다.</p>
      </div>
    );
  }

  // detail 정보를 기반으로 selectedInstitutions 구성
  const selectedInstitutions: InstitutionWithDatasets[] = [
    {
      provideInstitutionNo: detail.provideInstitutionNo,
      provideInstitutionName: detail.provideInstitutionName,
      type: detail.institutionType,
      datasets: detail.datasets,
    },
  ];

  // 전자서명 전에 데이터 삭제 여부 확인
  const handleBeforeSignature = (): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalResolve(() => resolve);
      setIsDataDeletionModalOpen(true);
    });
  };

  // 모달에서 확인 클릭
  const handleDataDeletionConfirm = (selectedDeleteData: boolean) => {
    setDeleteData(selectedDeleteData);
    setIsDataDeletionModalOpen(false);
    modalResolve?.(true); // 전자서명 진행
    setModalResolve(null);
  };

  // 모달 닫기 (취소)
  const handleDataDeletionClose = () => {
    setIsDataDeletionModalOpen(false);
    modalResolve?.(false); // 전자서명 중단
    setModalResolve(null);
  };

  // 전자서명 완료 시 API 호출
  const handleSignatureComplete = async (data: SignatureCompletionData) => {
    const signatureResult =
      data.provideInstitutionTransmissionList[0]?.signatureResult;

    if (!signatureResult) {
      throw new Error("전송 데이터가 올바르지 않습니다.");
    }

    await updateTransmissionDemand(
      transmissionDemandId!,
      signatureResult,
      deleteData
    );
  };

  return (
    <>
      <MyDataAgreementPage
        title="전송요구서 수정"
        onBack={goBack}
        successMessage="전송요구서가 성공적으로 수정되었습니다."
        buttonText="수정 완료"
        autoAgreeButtonText="모두 동의 및 수정"
        selectedInstitutions={selectedInstitutions}
        onBeforeSignature={handleBeforeSignature}
        onSignatureComplete={handleSignatureComplete}
        successNavigateTo="/settings/institution-connection"
      />

      {/* 데이터 삭제 확인 모달 */}
      <DataDeletionConfirmModal
        isOpen={isDataDeletionModalOpen}
        onClose={handleDataDeletionClose}
        onConfirm={handleDataDeletionConfirm}
        title="전송요구서 수정"
        description={`${detail.provideInstitutionName} 전송요구서를 수정합니다.\n기존 전송요구서와 관련된 데이터 처리 방식을 선택해주세요.`}
        keepDataDescription="채팅 이력과 기존 전송요구서로 수집한 의료 정보를 유지합니다."
        deleteDataDescription="채팅 이력과 기존 전송요구서로 수집한 의료 정보를 삭제합니다."
        confirmText="수정 진행"
        cancelText="취소"
      />
    </>
  );
};

export default TransmissionDemandEditPage;
